import React from 'react'

import { useLocation, useNavigate } from 'react-router-dom';
import QuoteConfirmationModal from '../components/QuoteConfirmationModal.jsx';
import useAnalytics from '../hooks/useAnalytics';

export default function BasketPage({ basket, onUpdateQuantity, onRemoveItem, user, onClearBasket, onShowLogin, onViewProduct }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { trackEvent, consent } = useAnalytics();
    const [isProcessing, setIsProcessing] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [showQuoteConfirmModal, setShowQuoteConfirmModal] = React.useState(false);
    const [quoteRequested, setQuoteRequested] = React.useState(false);
    const [actionSuccess, setActionSuccess] = React.useState(null);
    const [actionLoading, setActionLoading] = React.useState(null);

    const cleanPath = (path) => {
        if (!path) return null;
        if (path.match(/^(http|https|data):/i)) return path;
        let clean = decodeURIComponent(path).replace(/\\/g, '/').trim();
        if (clean.startsWith('/miners-market-main/')) {
            clean = clean.substring('/miners-market-main/'.length);
        }
        if (!clean.startsWith('/')) {
            clean = '/' + clean;
        }
        const anchorMatch = clean.match(/(uploads\/|images\/|miscellaneous items\/)/i);
        if (anchorMatch) {
            return clean.substring(anchorMatch.index - 1);
        }
        if (clean.includes(':')) return null;
        return clean;
    };

    // Determine the back navigation based on where user came from
    const getBackNavigation = () => {
        const referrer = document.referrer;
        const fromState = location.state?.from;
        
        if (fromState) {
            return { path: fromState, text: 'Back to Previous Page' };
        }
        
        const urlParams = new URLSearchParams(location.search);
        const fromCategory = urlParams.get('from');
        
        if (fromCategory === 'miscellaneous') {
            return { path: '/miscellaneous', text: 'Back to Miscellaneous' };
        } else if (fromCategory === 'equipment') {
            return { path: '/equipment', text: 'Back to Equipment' };
        } else if (fromCategory === 'consumables') {
            return { path: '/consumables', text: 'Back to Consumables' };
        }
        
        return { path: '/', text: 'Back to Marketplace' };
    };

    const backNavigation = getBackNavigation();

    // Track basket opened
    React.useEffect(() => {
        if (consent === 'granted') {
            trackEvent('basket_opened', {});
        }
    }, [consent, trackEvent]);

    const formatPrice = (price) => {
        const num = parseFloat(price);
        if (isNaN(num)) return 'R 0.00';
        return `R ${num.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // Read legacy and size-priced stock data without assuming JSON is always valid.
    const getSizeData = (item) => {
        if (!item.size) return {};
        if (typeof item.size === 'object') return item.size && !Array.isArray(item.size) ? item.size : {};
        try {
            const parsed = JSON.parse(item.size);
            return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
        } catch {
            return {};
        }
    };

    const getSizePrice = (item, size, sizeData = getSizeData(item)) => {
        const entry = sizeData[size];
        const override = entry && typeof entry === 'object' ? Number(entry.price) : NaN;
        return Number.isFinite(override) && override > 0 ? override : (Number(item.price) || 0);
    };

    const hasSelectedSizes = (item) => item.listingType === 'consumable' &&
        item.selectedSizes && typeof item.selectedSizes === 'object' &&
        !Array.isArray(item.selectedSizes) && Object.keys(item.selectedSizes).length > 0;

    const calculateTotal = () => {
        return basket.reduce((total, item) => {
            if (hasSelectedSizes(item)) {
                const sizeData = getSizeData(item);
                return total + Object.entries(item.selectedSizes).reduce(
                    (sum, [size, qty]) => sum + getSizePrice(item, size, sizeData) * (Number(qty) || 0), 0
                );
            }
            if (item.listingType === 'drill-enquiry') return total; // No price for drill enquiries
            const price = parseFloat(item.price) || 0;
            return total + (price * item.quantity);
        }, 0);
    };

    const handleCheckout = () => {
        setError("Online payment is currently unavailable. Please contact admin to complete your order.");
        setIsProcessing(false);
    };

    const handleRequestQuote = () => {
        if (!user && !basket.some(item => item.listingType === 'drill-enquiry')) {
            sessionStorage.setItem('redirectAfterLogin', '/basket');
            onShowLogin();
            return;
        }
        if (consent === 'granted') {
            trackEvent('quote_request_initiated', {});
        }
        setShowQuoteConfirmModal(true);
    };

    const confirmAndSendQuote = async (recipientEmail) => {
        setError(null);
        setActionSuccess(null);

        try {
            // Check for drill-enquiry items
            const drillEnquiryItems = basket.filter(item => item.listingType === 'drill-enquiry');
            const isDrillEnquiry = drillEnquiryItems.length > 0;
            
            // For drill enquiries, use the contact info from the enquiry item
            const drillEnquiry = isDrillEnquiry ? drillEnquiryItems[0] : null;
            const contactInfo = isDrillEnquiry ? drillEnquiry.contactInfo : null;
            const drillProducts = isDrillEnquiry ? drillEnquiry.products : [];
            
            // For drill enquiries, send to drillkon@minersmarket.co.za AND user's email
            const targetEmail = isDrillEnquiry ? 'drillkon@minersmarket.co.za' : recipientEmail;
            const recipientName = isDrillEnquiry ? (contactInfo?.name || 'Drill Catalogue Enquiry') : user.name;
            
            // For drill-enquiry, also send copy to user's email
            const userEmail = isDrillEnquiry ? (contactInfo?.email || recipientEmail) : recipientEmail;

            // Optimized image loading with timeout and error handling
            const loadImageWithTimeout = (src, timeout = 5000) => {
                return new Promise((resolve, reject) => {
                    const img = new Image();
                    const timer = setTimeout(() => {
                        console.warn(`Image load timeout: ${src}`);
                        resolve(null);
                    }, timeout);
                    
                    img.onload = () => {
                        clearTimeout(timer);
                        resolve(img);
                    };
                    
                    img.onerror = () => {
                        clearTimeout(timer);
                        console.warn(`Image load failed: ${src}`);
                        resolve(null);
                    };
                    
                    img.src = src;
                });
            };
                
            setActionLoading('Loading assets...');
            const minersMarketLogo = await loadImageWithTimeout('images/logo/MM - Logo.jpeg');
            const whatsappImage = await loadImageWithTimeout('quote_image/WhatsApp Image 2026-04-29 at 14.26.24.jpeg');
            
            // Load images for basket items (skip drill-enquiry items)
            const regularItems = basket.filter(item => item.listingType !== 'drill-enquiry');
            const basketItemImages = await Promise.all(
                regularItems.map(item => loadImageWithTimeout(item.image_url_1 || item.image_url || null))
            );

            // --- NEW: Create circular logo for contact section ---
            const logoCanvas = document.createElement('canvas');
            const logoSizePx = Math.min(minersMarketLogo.width, minersMarketLogo.height);
            logoCanvas.width = logoSizePx;
            logoCanvas.height = logoSizePx;
            const logoCtx = logoCanvas.getContext('2d');
            logoCtx.beginPath();
            logoCtx.arc(logoSizePx / 2, logoSizePx / 2, logoSizePx / 2, 0, Math.PI * 2, true);
            logoCtx.closePath();
            logoCtx.clip();
            logoCtx.drawImage(minersMarketLogo, 0, 0, logoSizePx, logoSizePx);
            const circularLogoData = logoCanvas.toDataURL('image/png');

            // 1. Generate the PDF on the client-side with optimizations
            const { jsPDF } = window.jspdf;
            const doc = new jsPDF();

            // --- PAGE 1: WhatsApp Image Cover Page ---
            if (whatsappImage) {
                setActionLoading('Adding cover page...');
                console.log('[PDF Generation] Adding WhatsApp image as first page');
                
                const pageWidth = doc.internal.pageSize.getWidth();
                const pageHeight = doc.internal.pageSize.getHeight();
                const imgAspectRatio = whatsappImage.width / whatsappImage.height;
                const pageAspectRatio = pageWidth / pageHeight;
                
                let imgWidth, imgHeight, imgX, imgY;
                
                if (imgAspectRatio > pageAspectRatio) {
                    imgWidth = pageWidth;
                    imgHeight = pageWidth / imgAspectRatio;
                    imgX = 0;
                    imgY = (pageHeight - imgHeight) / 2;
                } else {
                    imgHeight = pageHeight;
                    imgWidth = pageHeight * imgAspectRatio;
                    imgX = (pageWidth - imgWidth) / 2;
                    imgY = 0;
                }
                
                doc.addImage(whatsappImage, 'JPEG', imgX, imgY, imgWidth, imgHeight);
                
                doc.setFontSize(8);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(150);
                doc.text('Page 1', pageWidth / 2, pageHeight - 10, { align: 'center' });
            } else {
                console.warn('[PDF Generation] WhatsApp image not loaded, skipping cover page');
            }

            // --- PAGE 2: Main Content (Basket Items) ---
            doc.addPage();
            
            doc.addImage(minersMarketLogo, 'JPEG', 15, 15, 30, 30);
            doc.setFontSize(22);
            doc.setFont('helvetica', 'bold');
            
            const title = isDrillEnquiry ? 'DRILL CATALOGUE ENQUIRY' : 'OFFICIAL QUOTATION';
            doc.text(title, doc.internal.pageSize.getWidth() / 2, 25, { align: 'center' });
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(100);
            
            if (isDrillEnquiry) {
                doc.text(`Enquiry from: ${contactInfo?.name || '-'}`, 15, 50);
                doc.text(`Company: ${contactInfo?.company || '-'}`, 15, 57);
                doc.text(`Email: ${contactInfo?.email || '-'}`, 15, 64);
                doc.text(`Phone: ${contactInfo?.phone || '-'}`, 15, 71);
            } else {
                doc.text(`Quote for: ${user.name}`, 15, 50);
            }
            doc.text(`Date: ${new Date().toLocaleDateString()}`, doc.internal.pageSize.getWidth() - 15, 50, { align: 'right' });

            // --- Basket Items Table ---
            const tableColumn = isDrillEnquiry 
                ? ["Category", "Product", "Qty"] 
                : ["Description", "Listing #", "Qty", "Unit Price", "Total"];
            
            let tableRows = [];
            
            if (isDrillEnquiry) {
                // Drill catalogue enquiry - show all products with categories
                drillProducts.forEach(product => {
                    tableRows.push([product.category, product.name, String(product.quantity)]);
                });
                
                // Add message if present
                if (contactInfo?.message?.trim()) {
                    tableRows.push(["", "Additional Message:", ""]);
                    tableRows.push(["", contactInfo.message.trim(), ""]);
                }
            } else {
                // Regular basket items with pricing
                tableRows = regularItems.map(item => {
                    let price, lineTotal;
                    if (hasSelectedSizes(item)) {
                        const sizeData = getSizeData(item);
                        return Object.entries(item.selectedSizes).map(([size, qty]) => {
                            const sizePrice = getSizePrice(item, size, sizeData);
                            return [`${item.name} (${size})`, item.listing_number, qty,
                                formatPrice(sizePrice), formatPrice(sizePrice * (Number(qty) || 0))];
                        });
                    } else {
                        price = parseFloat(item.price) || 0;
                        lineTotal = price * item.quantity;
                        return [[item.name, item.listing_number, item.quantity, formatPrice(price), formatPrice(lineTotal)]];
                    }
                }).flat();
            }

            if (!isDrillEnquiry) {
                // Calculate totals for regular items
                const subtotal = calculateTotal();
                const vat = subtotal * 0.15;
                const total = subtotal + vat;

                tableRows.push(["", "", "", "Subtotal", formatPrice(subtotal)]);
                tableRows.push(["", "", "", "VAT (15%)", formatPrice(vat)]);
                tableRows.push(["", "", "", "Grand Total", formatPrice(total)]);
            }

            doc.autoTable({
                head: [tableColumn],
                body: tableRows,
                startY: isDrillEnquiry ? 80 : 60,
                headStyles: { fillColor: [22, 160, 133] },
                styles: { textColor: [33, 33, 33], fontSize: isDrillEnquiry ? 9 : 10 },
                columnStyles: isDrillEnquiry ? {
                    0: { cellWidth: 50 },
                    1: { cellWidth: 100 },
                    2: { cellWidth: 20, halign: 'center' }
                } : {}
            });
            const finalY = doc.autoTable.previous.finalY;

            // Add product images for regular basket items
            const availableImages = basketItemImages.filter(img => img !== null);
            if (availableImages.length > 0 && !isDrillEnquiry) {
                const imgSize = 50;
                const imgGap = 10;
                const imagesPerRow = 4;
                const numberOfRows = Math.ceil(availableImages.length / imagesPerRow);
                const requiredHeight = 10 + (numberOfRows * (imgSize + imgGap));
                
                let imgY = finalY + 15;
                
                if (imgY + requiredHeight > 280) {
                    doc.addPage();
                    imgY = 20;
                }
                
                doc.setFontSize(10);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(33, 33, 33);
                doc.text('Product Images:', 15, imgY - 5);
                
                let imgX = 15;
                
                availableImages.forEach((img, index) => {
                    if (index > 0 && index % imagesPerRow === 0) {
                        imgX = 15;
                        imgY += imgSize + imgGap;
                        
                        if (imgY + imgSize > 280) {
                            doc.addPage();
                            imgY = 20;
                        }
                    }
                    
                    if (imgX + imgSize <= 195) {
                        doc.addImage(img, 'JPEG', imgX, imgY, imgSize, imgSize);
                        imgX += imgSize + imgGap;
                    }
                });
            }

            // --- LAST PAGE: Banking Details and Terms & Conditions ---
            doc.addPage();
            let lastPageY = 20;
            console.log('[PDF Generation] Adding banking details and terms as last page');

            // Banking Details Section
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(33, 33, 33);
            doc.text('Banking Details:', 15, lastPageY);
            console.log('[PDF Generation] Adding banking details');
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            
            doc.text('Bank Name: FIRST NATIONAL BANK', 15, lastPageY + 7);
            doc.text('Account Type: GOLD BUSINESS ACCOUNT', 15, lastPageY + 14);
            doc.text('Account Number: 63204739205', 15, lastPageY + 21);
            doc.text('Branch Code: 258155', 15, lastPageY + 28);
            doc.text('Branch Name: FNB POP BRANCH DOLYMPUS', 15, lastPageY + 35);
            doc.text('SWIFT Code: FIRNZAJJ', 15, lastPageY + 42);
            doc.setFont('helvetica', 'bold');
            doc.text('Payment Reference: Please use quote reference', 15, lastPageY + 49);

            // Terms and Conditions Section
            lastPageY += 65;
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(33, 33, 33);
            doc.text('Terms and Conditions:', 15, lastPageY);
            doc.setFont('helvetica', 'italic');
            doc.setFontSize(7);
            doc.setTextColor(100, 100, 100);
            doc.text("PLEASE USE LINK TO VIEW", 195, lastPageY, { align: 'right' });

            doc.setLineWidth(0.5);
            doc.setDrawColor(200, 200, 200);
            doc.line(15, lastPageY + 2, 195, lastPageY + 2);
            lastPageY += 10;
            
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(60, 60, 60);

            const addTermSection = (title, content) => {
                doc.setFont('helvetica', 'bold');
                doc.text(title, 15, lastPageY);
                lastPageY += 5;
                doc.setFont('helvetica', 'normal');
                const lines = doc.splitTextToSize(content, 180);
                doc.text(lines, 15, lastPageY);
                lastPageY += (lines.length * 4) + 4;
            };

            addTermSection('Quotation Currency', 'THIS QUOTATION IS CALCULATED IN SOUTHERN AFRICAN CURRENCY (ZAR)');
            
            addTermSection('Pricing', '• Prices quoted include VAT.\n• Any optional accessories, add-ons, or customisations requested by customer will be quoted separately and may incur additional charges.');

            addTermSection('Validity', '• This quotation is valid for five (5) days from date of issue.\n• Prices may be subject to change after the validity period expires.');

            addTermSection('Payment Terms', '• Accepted payment methods:\n   o Bank Transfer (EFT)\n   o Credit Card\n   o Cheque\n   (Trade Safe Payment Options Available via Miners Market)\n• Full payment is required before machine/equipment will be released.');

            addTermSection('Delivery', '• Delivery arrangements, costs, and timelines will be discussed and confirmed with Miners Market team upon POP confirmation. Inspection of equipment/spares can be arranged on request.');

            addTermSection('General', '• This quotation is subject to Miners Market\'s standard terms and conditions.\n• Availability of equipment is subject to prior sale.');

            lastPageY += 5;
            doc.setFont('helvetica', 'italic');
            const closingText = doc.splitTextToSize('We look forward to doing business with you and building a long-standing working relationship with you and your company.', 180);
            doc.text(closingText, 15, lastPageY);
            lastPageY += (closingText.length * 4) + 10;

            // Contact Section
            if (lastPageY > 240) {
                doc.addPage();
                lastPageY = 20;
            }
            console.log('[PDF Generation] Adding contact section');

            doc.setFillColor(248, 249, 250);
            doc.setDrawColor(220, 220, 220);
            doc.rect(15, lastPageY, 180, 45, 'FD');
            
            const footerLogoSize = 25;
            const footerLogoX = 20;
            const footerLogoY = lastPageY + 10;
            doc.addImage(circularLogoData, 'PNG', footerLogoX, footerLogoY, footerLogoSize, footerLogoSize);
            doc.setDrawColor(200, 200, 200);
            doc.setLineWidth(0.5);
            doc.circle(footerLogoX + footerLogoSize / 2, footerLogoY + footerLogoSize / 2, footerLogoSize / 2, 'S');

            let contactY = lastPageY + 8;
            const col1 = 50;
            const col2 = 115;

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            doc.setTextColor(33, 33, 33);
            doc.text('The Miners Market Team', col1, contactY);
            
            contactY += 8;
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            
            doc.text('Phone: +27 82 455 1658', col1, contactY);
            contactY += 5;
            doc.text('Email: info@minersmarket.co.za (General Enquiries)', col1, contactY);
            contactY += 5;
            doc.text('Email: finance@minersmarket.co.za (Finance)', col1, contactY);
            contactY += 5;
            
            let socialX = col1;
            doc.text('Social: ', socialX, contactY);
            socialX += doc.getTextWidth('Social: ');
            
            doc.setTextColor(0, 0, 255);
            doc.text('LinkedIn', socialX, contactY, { url: 'https://www.linkedin.com/company/miners-market/posts/?feedView=all' });
            socialX += doc.getTextWidth('LinkedIn');
            
            doc.setTextColor(33, 33, 33);
            doc.text(' | ', socialX, contactY);
            socialX += doc.getTextWidth(' | ');
            
            doc.setTextColor(0, 0, 255);
            doc.text('Facebook', socialX, contactY, { url: 'https://www.facebook.com/people/Miners-Market/61581241269225/' });
            socialX += doc.getTextWidth('Facebook');
            
            doc.setTextColor(33, 33, 33);
            doc.text(' | ', socialX, contactY);
            socialX += doc.getTextWidth(' | ');
            
            doc.setTextColor(0, 0, 255);
            doc.text('WhatsApp', socialX, contactY, { url: 'https://whatsapp.com/channel/0029VbAgvIQHAdNVnXCaN30z' });
            
            doc.setTextColor(33, 33, 33);

            contactY = lastPageY + 16;
            doc.text('Web: www.minersmarket.co.za', col2, contactY, { url: 'https://www.minersmarket.co.za' });
            contactY += 5;
            doc.text('Email: quotes@minersmarket.co.za (Quotations)', col2, contactY);
            contactY += 5;
            doc.text('Email: support@minersmarket.co.za (Technical)', col2, contactY);

            // Add page number at bottom
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(150);
            const totalPages = doc.internal.getNumberOfPages();
            doc.text(`Page ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });

            setActionLoading('Finalizing document...');
            const pdfData = doc.output('datauristring').split(',')[1];
            console.log('[PDF Generation] PDF data generated, length:', pdfData.length, 'bytes');

            // Send email
            setActionLoading('Processing quote request...');
            
            const sendQuoteEmail = async (recipient_email, recipient_name) => {
                const response = await fetch('/api/send_quote_email_async.php', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        recipient_email: recipient_email,
                        recipient_name: recipient_name,
                        product_id: isDrillEnquiry ? drillProducts.map(p => p.partNumber).join(',') : basket.map(item => item.id).join(','),
                        listing_type: isDrillEnquiry ? 'drill-enquiry' : 'basket',
                        product_name: isDrillEnquiry ? 'Drill Catalogue Enquiry' : 'Multiple Items',
                        listing_number: isDrillEnquiry ? `DRILL-ENQ-${Date.now()}` : 'BASKET',
                        pdf_data: pdfData,
                        // Pass contact info for drill enquiries
                        ...(isDrillEnquiry && { contact_info: contactInfo })
                    })
                });
                const result = await response.json();
                if (!response.ok) throw new Error(result.error || 'Failed to send quote email.');
                return result;
            };
            
            if (isDrillEnquiry) {
                // Send to drillkon@minersmarket.co.za
                await sendQuoteEmail('drillkon@minersmarket.co.za', contactInfo?.name || 'Drill Catalogue Enquiry');
                // Also send copy to user's email
                await sendQuoteEmail(userEmail, contactInfo?.name || 'Drill Catalogue Enquiry');
                setActionSuccess('Your quote has been sent to drillkon@minersmarket.co.za and ' + userEmail);
            } else {
                await sendQuoteEmail(targetEmail, recipientName);
                setActionSuccess('Your quote has been sent to ' + targetEmail);
            }

            setQuoteRequested(true);
            if (consent === 'granted') {
                trackEvent('quote_request_submitted', {
                    eventData: {
                        itemCount: basket.length,
                        totalValue: calculateTotal ? calculateTotal() : 0,
                    },
                });
            }
            setShowQuoteConfirmModal(false);
        } catch (err) {
            setError(err.message);
            setActionLoading(false);
        }
    };

    if (basket.length === 0) {
        return (
            <div className="min-h-screen py-12 flex flex-col items-center justify-center">
                <i className="fas fa-shopping-basket text-6xl text-gray-300 mb-4"></i>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Your Basket is Empty</h2>
                <p className="text-gray-600 mb-8">Looks like you haven't added any items yet.</p>
                <button onClick={() => navigate(backNavigation.path)} className="primary-gradient text-white font-bold py-3 px-8 rounded-lg hover:opacity-90">
                    {backNavigation.text}
                </button>
            </div>
        );
    }

    // Check if basket only has drill-enquiry (no pricing)
    const hasRegularItems = basket.some(item => item.listingType !== 'drill-enquiry');
    const drillEnquiry = basket.find(item => item.listingType === 'drill-enquiry');
    const isDrillEnquiry = !!drillEnquiry;

    return (
        <div data-name="basket-page" data-file="pages/BasketPage.js" className="container mx-auto px-4 py-8 min-h-screen">
            <h1 className="text-3xl font-bold text-white mb-8">Shopping Basket</h1>
            
            {/* Show drill enquiry contact info at top if present */}
            {drillEnquiry && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                    <h3 className="font-bold text-orange-800 mb-2">Drill Catalogue Enquiry</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        <p><span className="font-medium">Name:</span> {drillEnquiry.contactInfo?.name}</p>
                        <p><span className="font-medium">Company:</span> {drillEnquiry.contactInfo?.company || '-'}</p>
                        <p><span className="font-medium">Email:</span> {drillEnquiry.contactInfo?.email}</p>
                        <p><span className="font-medium">Phone:</span> {drillEnquiry.contactInfo?.phone || '-'}</p>
                        <p className="md:col-span-2"><span className="font-medium">Products:</span> {drillEnquiry.products?.length || 0} items</p>
                        {drillEnquiry.contactInfo?.message && (
                            <p className="md:col-span-2"><span className="font-medium">Message:</span> {drillEnquiry.contactInfo.message}</p>
                        )}
                    </div>
                    <p className="text-xs text-orange-600 mt-2">Quote will be sent to drillkon@minersmarket.co.za</p>
                </div>
            )}

            <div className="grid md:grid-cols-3 gap-8">
                {/* Items List */}
                <div className="md:col-span-2 space-y-4">
                    {basket.map(item => {
                        // Special rendering for drill-enquiry items
                        if (item.listingType === 'drill-enquiry') {
                            return (
                                <div key={`${item.id}-${item.listingType}`} className="bg-white rounded-xl shadow-md p-4">
                                    <h3 className="font-bold text-gray-900">{item.name}</h3>
                                    <p className="text-sm text-gray-500">{item.listing_number}</p>
                                    <div className="mt-2 space-y-1">
                                        {item.products?.map((product, idx) => (
                                            <div key={idx} className="text-sm text-gray-700 flex justify-between">
                                                <span>{product.category} - {product.name}</span>
                                                <span className="font-medium">Qty: {product.quantity}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="flex items-center gap-3 mt-3">
                                        <button 
                                            onClick={() => onRemoveItem(item.id, item.listingType)}
                                            className="text-red-500 hover:text-red-700 p-2"
                                            title="Remove enquiry"
                                        >
                                            <i className="fas fa-trash"></i>
                                        </button>
                                    </div>
                                </div>
                            );
                        }

                        const sized = hasSelectedSizes(item);
                        const sizeData = sized ? getSizeData(item) : {};
                        const itemTotal = sized ? Object.entries(item.selectedSizes).reduce(
                            (sum, [size, qty]) => sum + getSizePrice(item, size, sizeData) * (Number(qty) || 0), 0
                        ) : (Number(item.price) || 0) * (Number(item.quantity) || 0);
                        return (
                            <div key={`${item.id}-${item.listingType}`} className="bg-white rounded-xl shadow-md p-4 flex flex-col sm:flex-row items-center gap-4">
                                <img src={cleanPath(item.image_url_1) || 'https://via.placeholder.com/150'} alt={item.name} className="w-24 h-24 object-cover rounded-lg" />
                                <div className="flex-grow text-center sm:text-left">
                                    <h3 className="font-bold text-gray-900">{item.name}</h3>
                                    <p className="text-sm text-gray-500">{item.listing_number}</p>
                                    {sized ? (
                                        <div className="mt-2 space-y-1">
                                            {Object.entries(item.selectedSizes).map(([size, qty]) => {
                                                const sizePrice = getSizePrice(item, size, sizeData);
                                                return (
                                                    <p key={size} className="text-sm text-blue-600">
                                                        {size}: {qty} × {formatPrice(sizePrice)} = {formatPrice(sizePrice * (Number(qty) || 0))}
                                                    </p>
                                                );
                                            })}
                                            <p className="font-semibold text-blue-700 pt-1">
                                                Product total: {formatPrice(itemTotal)}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                To add more sizes or quantities, open the product details.
                                            </p>
                                        </div>
                                    ) : (
                                        <p className="text-blue-600 font-semibold mt-1">
                                            {formatPrice(item.price)} per unit · Total: {formatPrice(itemTotal)}
                                        </p>
                                    )}
                                </div>
                            {sized ? (
                                <div className="text-sm text-gray-700 font-semibold text-center sm:text-left">
                                    Qty: {Object.values(item.selectedSizes).reduce((sum, qty) => sum + (Number(qty) || 0), 0)}
                                </div>
                            ) : (
                            <div className="flex items-center gap-3">
                                <button 
                                    onClick={() => onUpdateQuantity(item.id, item.listingType, item.quantity - 1)}
                                    className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600"
                                    disabled={item.quantity <= 1}
                                >
                                    <i className="fas fa-minus text-xs"></i>
                                </button>
                                <span className="font-semibold w-8 text-center">{item.quantity}</span>
                                <button 
                                    onClick={() => onUpdateQuantity(item.id, item.listingType, item.quantity + 1)}
                                    className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600"
                                >
                                    <i className="fas fa-plus text-xs"></i>
                                </button>
                            </div>
                            )}
                            <button 
                                onClick={() => onViewProduct(item.id, item.listingType)}
                                className="text-blue-500 hover:text-blue-700 p-2 mr-2"
                                title="View item details"
                            >
                                <i className="fas fa-eye"></i>
                            </button>
                            <button 
                                onClick={() => onRemoveItem(item.id, item.listingType)}
                                className="text-red-500 hover:text-red-700 p-2"
                                title="Remove item"
                            >
                                <i className="fas fa-trash"></i>
                            </button>
                        </div>
                    );
                    })}
                    <div className="flex justify-end">
                        <button onClick={onClearBasket} className="text-red-500 hover:text-red-700 text-sm font-medium">
                            Clear Basket
                        </button>
                    </div>
                </div>

                {/* Summary */}
                <div className="md:col-span-1">
                    <div className="bg-white rounded-xl shadow-lg p-6 sticky top-24">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Order Summary</h2>
                        <div className="space-y-3 mb-6 border-b pb-6">
                            <div className="flex justify-between text-gray-600">
                                <span>Subtotal ({basket.reduce((acc, i) => acc + i.quantity, 0)} items)</span>
                                <span>{formatPrice(calculateTotal())}</span>
                            </div>
                        </div>
                        {hasRegularItems && (
                            <div className="flex justify-between items-center mb-6">
                                <span className="text-lg font-bold text-gray-900">Total</span>
                                <span className="text-2xl font-bold text-blue-600">{formatPrice(calculateTotal())}</span>
                            </div>
                        )}
                        {!hasRegularItems && (
                            <div className="text-center text-gray-600 mb-4">
                                <p>Drill Catalogue Enquiry - No pricing</p>
                                <p className="text-sm">Quote will be emailed to drillkon@minersmarket.co.za</p>
                            </div>
                        )}
                        
                        {error && <div className="bg-red-100 text-red-700 p-3 rounded-lg text-sm mb-4">{error}</div>}
                        {actionSuccess && <div className="bg-green-100 text-green-700 p-3 rounded-lg text-sm mb-4">{actionSuccess}</div>}

                        <div className="space-y-3">
                            <button 
                                onClick={handleRequestQuote}
                                disabled={isProcessing || quoteRequested}
                                className="w-full bg-blue-600 text-white font-bold py-4 rounded-lg hover:bg-blue-700 transition-opacity disabled:opacity-50"
                            >
                                {quoteRequested ? <><i className="fas fa-check mr-2"></i>Quote Sent</> : (isDrillEnquiry && !hasRegularItems ? 'Send Drill Enquiry' : 'Request Quote for All Items')}
                            </button>

                            {isDrillEnquiry && !hasRegularItems && (
                                <button
                                    onClick={() => navigate('/drill-catalog')}
                                    className="w-full bg-gray-600 text-white font-bold py-4 rounded-lg hover:bg-gray-700 transition-colors"
                                >
                                    <i className="fas fa-drill mr-2"></i>
                                    Continue Shopping Drills
                                </button>
                            )}

                            <p className="text-xs text-gray-500 text-center">
                                Contact admin for purchase assistance
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            {showQuoteConfirmModal && (
                <QuoteConfirmationModal
                    isOpen={showQuoteConfirmModal}
                    onClose={() => setShowQuoteConfirmModal(false)}
                    userEmail={drillEnquiry?.contactInfo?.email || user?.email}
                    onConfirm={confirmAndSendQuote}
                />
            )}
        </div>
    );
}